import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, Building2, Users, Globe, Shield, Zap, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import MarketingHeader from "@/components/layout/MarketingHeader";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";

const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(true);

  const plans = [
    {
      name: t("pricing.plans.starter.name", "Starter"),
      description: t("pricing.plans.starter.description", "Perfect for small teams starting with remote work"),
      monthlyPrice: 299,
      annualPrice: 249,
      employees: t("pricing.plans.starter.employees", "Up to 50 employees"),
      features: [
        t("pricing.features.basicDashboard", "Basic HR Dashboard"),
        t("pricing.features.countryAccess10", "10 Countries access"),
        t("pricing.features.emailSupport", "Email support"),
        t("pricing.features.basicCompliance", "Basic compliance alerts"),
        t("pricing.features.nestPointsBasic", "Nest Points system"),
      ],
      highlighted: false,
      cta: t("pricing.cta.getStarted", "Get Started"),
    },
    {
      name: t("pricing.plans.professional.name", "Professional"),
      description: t("pricing.plans.professional.description", "For growing companies with global teams"),
      monthlyPrice: 599,
      annualPrice: 499,
      employees: t("pricing.plans.professional.employees", "Up to 200 employees"),
      features: [
        t("pricing.features.fullDashboard", "Full HR Dashboard"),
        t("pricing.features.countryAccess50", "50+ Countries access"),
        t("pricing.features.prioritySupport", "Priority support"),
        t("pricing.features.advancedCompliance", "Advanced compliance tracking"),
        t("pricing.features.nestPointsAdvanced", "Advanced Nest Points governance"),
        t("pricing.features.ssoIntegration", "SSO Integration"),
        t("pricing.features.apiAccess", "API Access"),
      ],
      highlighted: true,
      cta: t("pricing.cta.startTrial", "Start Free Trial"),
    },
    {
      name: t("pricing.plans.enterprise.name", "Enterprise"),
      description: t("pricing.plans.enterprise.description", "Custom solutions for large organizations"),
      monthlyPrice: null,
      annualPrice: null,
      employees: t("pricing.plans.enterprise.employees", "Unlimited employees"),
      features: [
        t("pricing.features.everythingPro", "Everything in Professional"),
        t("pricing.features.unlimitedCountries", "Unlimited countries"),
        t("pricing.features.dedicatedManager", "Dedicated account manager"),
        t("pricing.features.customIntegrations", "Custom integrations"),
        t("pricing.features.slaGuarantee", "SLA guarantees"),
        t("pricing.features.onPremise", "On-premise deployment option"),
        t("pricing.features.customTraining", "Custom training & onboarding"),
      ],
      highlighted: false,
      cta: t("pricing.cta.contactSales", "Contact Sales"),
    },
  ];

  const comparisonFeatures = [
    { 
      category: t("pricing.comparison.categories.core", "Core Features"),
      features: [
        { name: t("pricing.comparison.hrDashboard", "HR Dashboard"), starter: true, professional: true, enterprise: true },
        { name: t("pricing.comparison.employeePortal", "Employee Portal"), starter: true, professional: true, enterprise: true },
        { name: t("pricing.comparison.nestPoints", "Nest Points System"), starter: true, professional: true, enterprise: true },
        { name: t("pricing.comparison.requestWorkflow", "Request Workflow"), starter: true, professional: true, enterprise: true },
      ]
    },
    {
      category: t("pricing.comparison.categories.compliance", "Compliance & Security"),
      features: [
        { name: t("pricing.comparison.countryCompliance", "Country Compliance Data"), starter: "10", professional: "50+", enterprise: t("pricing.comparison.unlimited", "Unlimited") },
        { name: t("pricing.comparison.riskAlerts", "Risk Alerts"), starter: t("pricing.comparison.basic", "Basic"), professional: t("pricing.comparison.advanced", "Advanced"), enterprise: t("pricing.comparison.custom", "Custom") },
        { name: t("pricing.comparison.taxTracking", "Tax Residency Tracking"), starter: false, professional: true, enterprise: true },
        { name: t("pricing.comparison.auditLogs", "Audit Logs"), starter: false, professional: true, enterprise: true },
      ]
    },
    {
      category: t("pricing.comparison.categories.support", "Support & Integration"),
      features: [
        { name: t("pricing.comparison.supportLevel", "Support Level"), starter: t("pricing.comparison.email", "Email"), professional: t("pricing.comparison.priority", "Priority"), enterprise: t("pricing.comparison.dedicated", "Dedicated") },
        { name: t("pricing.comparison.ssoIntegration", "SSO Integration"), starter: false, professional: true, enterprise: true },
        { name: t("pricing.comparison.apiAccess", "API Access"), starter: false, professional: true, enterprise: true },
        { name: t("pricing.comparison.customIntegrations", "Custom Integrations"), starter: false, professional: false, enterprise: true },
      ]
    },
  ];

  return (
    <>
      <SEO 
        title={t("pricing.seo.title", "Pricing - Renest")}
        description={t("pricing.seo.description", "Flexible pricing plans for companies of all sizes. Start managing remote work compliance today.")}
      />
      <div className="min-h-screen bg-background">
        <MarketingHeader />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <Badge variant="outline" className="mb-4">
              <Zap className="h-3 w-3 mr-1" />
              {t("pricing.badge", "Simple, transparent pricing")}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {t("pricing.title", "Choose the plan that fits your team")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              {t("pricing.subtitle", "Start free, scale as you grow. All plans include our core compliance features.")}
            </p>
            
            {/* Annual/Monthly Toggle */}
            <div className="flex items-center justify-center gap-3 mb-12">
              <Label htmlFor="billing-toggle" className={!isAnnual ? "font-semibold" : "text-muted-foreground"}>
                {t("pricing.monthly", "Monthly")}
              </Label>
              <Switch
                id="billing-toggle"
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
              />
              <Label htmlFor="billing-toggle" className={isAnnual ? "font-semibold" : "text-muted-foreground"}>
                {t("pricing.annual", "Annual")}
              </Label>
              {isAnnual && (
                <Badge variant="secondary" className="ml-2">
                  {t("pricing.savePercent", "Save 20%")}
                </Badge>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <Card 
                  key={index} 
                  className={`relative ${plan.highlighted ? 'border-primary shadow-lg scale-105' : ''}`}
                >
                  {plan.highlighted && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                      <Star className="h-3 w-3 mr-1" />
                      {t("pricing.mostPopular", "Most Popular")}
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="text-center">
                      {plan.monthlyPrice ? (
                        <>
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-4xl font-bold">
                              €{isAnnual ? plan.annualPrice : plan.monthlyPrice}
                            </span>
                            <span className="text-muted-foreground">/{t("pricing.perMonth", "mo")}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {plan.employees}
                          </p>
                        </>
                      ) : (
                        <div className="py-2">
                          <span className="text-3xl font-bold">{t("pricing.custom", "Custom")}</span>
                          <p className="text-sm text-muted-foreground mt-1">
                            {plan.employees}
                          </p>
                        </div>
                      )}
                    </div>

                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-success shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button 
                      className="w-full" 
                      variant={plan.highlighted ? "default" : "outline"}
                      onClick={() => navigate('/contact')}
                    >
                      {plan.cta}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                {t("pricing.comparison.title", "Compare Plans")}
              </h2>
              <p className="text-muted-foreground">
                {t("pricing.comparison.subtitle", "See which plan is right for your organization")}
              </p>
            </div>

            <div className="bg-card rounded-xl border overflow-hidden">
              <div className="grid grid-cols-4 bg-muted/50 border-b">
                <div className="p-4 font-medium">{t("pricing.comparison.feature", "Feature")}</div>
                <div className="p-4 font-medium text-center">{t("pricing.plans.starter.name", "Starter")}</div>
                <div className="p-4 font-medium text-center bg-primary/5">{t("pricing.plans.professional.name", "Professional")}</div>
                <div className="p-4 font-medium text-center">{t("pricing.plans.enterprise.name", "Enterprise")}</div>
              </div>

              {comparisonFeatures.map((category, catIndex) => (
                <div key={catIndex}>
                  <div className="bg-muted/30 px-4 py-2 font-semibold text-sm border-b">
                    {category.category}
                  </div>
                  {category.features.map((feature, featIndex) => (
                    <div key={featIndex} className="grid grid-cols-4 border-b last:border-b-0">
                      <div className="p-4 text-sm">{feature.name}</div>
                      <div className="p-4 text-center">
                        {typeof feature.starter === 'boolean' ? (
                          feature.starter ? <Check className="h-5 w-5 text-success mx-auto" /> : <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="text-sm">{feature.starter}</span>
                        )}
                      </div>
                      <div className="p-4 text-center bg-primary/5">
                        {typeof feature.professional === 'boolean' ? (
                          feature.professional ? <Check className="h-5 w-5 text-success mx-auto" /> : <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="text-sm">{feature.professional}</span>
                        )}
                      </div>
                      <div className="p-4 text-center">
                        {typeof feature.enterprise === 'boolean' ? (
                          feature.enterprise ? <Check className="h-5 w-5 text-success mx-auto" /> : <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="text-sm">{feature.enterprise}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enterprise CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center gap-2 mb-4 justify-center md:justify-start">
                      <Building2 className="h-6 w-6 text-primary" />
                      <span className="font-semibold text-lg">{t("pricing.enterprise.badge", "Enterprise Solutions")}</span>
                    </div>
                    <h3 className="text-2xl font-bold mb-3">
                      {t("pricing.enterprise.title", "Need a custom solution?")}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {t("pricing.enterprise.description", "Our team will work with you to create a tailored solution that meets your organization's unique compliance and remote work needs.")}
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                      <div className="flex items-center gap-2 text-sm">
                        <Shield className="h-4 w-4 text-primary" />
                        <span>{t("pricing.enterprise.features.security", "Enterprise security")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-primary" />
                        <span>{t("pricing.enterprise.features.onboarding", "Custom onboarding")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="h-4 w-4 text-primary" />
                        <span>{t("pricing.enterprise.features.global", "Global coverage")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <Button size="lg" onClick={() => navigate('/contact')}>
                      {t("pricing.enterprise.cta", "Talk to Sales")}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Preview */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 border-t">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-xl font-semibold mb-4">
              {t("pricing.faq.title", "Have questions?")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t("pricing.faq.description", "Our team is here to help you find the right plan for your organization.")}
            </p>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              {t("pricing.faq.cta", "Contact us")}
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default Pricing;
